import {
  Button,
  Circle,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  useBreakpointValue,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiRotateCcw } from 'react-icons/fi';

import PhotoAnnotatorCanvas, {
  PhotoAnnotatorRef,
} from './PhotoAnnotatorCanvas.component';

const COLORS = [
  '#08b1d5', // blue
  '#EB5757', // red
  '#FFC013', // yellow
  '#7FC008', // green
  '#000000', // black
  '#ffffff', // white
];

export type PhotoAnnotatorModalProps = {
  isOpen: boolean;
  photoSrc: string | null;
  onClose: () => void;
  onConfirm: (dataUrl: string | null) => void | Promise<void>;
};

const PhotoAnnotatorModal: React.FC<PhotoAnnotatorModalProps> = ({
  isOpen,
  photoSrc,
  onClose,
  onConfirm,
}) => {
  const canvasRef = React.useRef<PhotoAnnotatorRef>(null);
  const [color, setColor] = React.useState(COLORS[0]);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const modalSize = useBreakpointValue({ base: 'full', lg: '2xl' });
  const { t } = useTranslation();

  const handleConfirm = React.useCallback(async () => {
    setIsConfirming(true);
    try {
      const dataUrl = (await canvasRef.current?.onSave()) ?? null;
      await onConfirm(dataUrl);
    } finally {
      setIsConfirming(false);
    }
  }, [onConfirm]);

  if (!photoSrc) return null;

  return (
    <Modal
      closeOnEsc={!isConfirming}
      closeOnOverlayClick={!isConfirming}
      size={modalSize}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      <Portal>
        <ModalContent>
          <ModalHeader>Preview da Imagem</ModalHeader>
          <ModalCloseButton disabled={isConfirming} />
          <ModalBody flexDir="column" display="flex">
            <PhotoAnnotatorCanvas
              ref={canvasRef}
              photoSrc={photoSrc}
              color={color}
            />
          </ModalBody>

          <ModalFooter justifyContent="space-between">
            <HStack spacing="2">
              {COLORS.map((c) => (
                <Circle
                  key={c}
                  as="button"
                  type="button"
                  size="6"
                  bg={c}
                  borderWidth={color === c ? '2px' : '1px'}
                  borderColor={color === c ? 'brand.500' : 'gray.300'}
                  onClick={() => setColor(c)}
                  aria-label={`color-${c}`}
                />
              ))}
              <IconButton
                disabled={isConfirming}
                onClick={() => canvasRef.current?.onClear()}
                icon={<FiRotateCcw />}
                aria-label="clear-drawing"
                size="sm"
              />
            </HStack>
            <HStack>
              <Button
                isDisabled={isConfirming}
                translate="no"
                onClick={onClose}
                variant="ghost"
              >
                {t('common.cancel')}
              </Button>
              <Button
                isLoading={isConfirming}
                colorScheme="brand"
                onClick={handleConfirm}
                translate="no"
              >
                {t('common.confirm')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Portal>
    </Modal>
  );
};

export default PhotoAnnotatorModal;
